import React from 'react';
import axios from 'axios';

/**
 * Msg #996 — Inventory tab body.
 *
 * Two sections:
 *   1) ITEMS — up to 30 image URL slots (each with optional name).
 *   2) CAUGHT POKÉMON — read-only list of Pokémon/Pokémorphs captured with
 *      a Poké Ball. Shows live affection & dislike per NPC.
 */
export const InventoryTabBody = ({ apiBase, username }) => {
  const [items, setItems] = React.useState([]);        // [{url, name}]
  const [pokemon, setPokemon] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [note, setNote] = React.useState('');

  const MAX = 30;

  const seededSlots = React.useCallback((rows) => {
    const arr = Array.from({ length: MAX }, (_, i) => rows[i] || { url: '', name: '' });
    return arr;
  }, []);

  const load = React.useCallback(async () => {
    if (!username) return;
    setLoading(true); setErr(''); setNote('');
    try {
      const r = await axios.get(`${apiBase}/inventory`, { params: { username } });
      setItems(seededSlots(r.data?.items || []));
      setPokemon(r.data?.pokemon || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d || 'Failed to load inventory.');
    } finally { setLoading(false); }
  }, [apiBase, username, seededSlots]);

  React.useEffect(() => { load(); }, [load]);

  const updateSlot = (idx, key, val) => {
    setItems((prev) => {
      const next = prev.slice();
      next[idx] = { ...(next[idx] || { url: '', name: '' }), [key]: val };
      return next;
    });
  };
  const clearSlot = (idx) => updateSlot(idx, 'url', '') || updateSlot(idx, 'name', '');

  const save = async () => {
    setBusy(true); setErr(''); setNote('');
    try {
      const cleaned = items
        .map((s) => ({ url: (s.url || '').trim(), name: (s.name || '').trim() }))
        .filter((s) => s.url);
      const r = await axios.post(`${apiBase}/inventory/save`, {
        username, items: cleaned,
      });
      setNote(`Saved — ${r.data?.count ?? 0} items stored.`);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d || 'Save failed.');
    } finally { setBusy(false); }
  };

  const filledCount = items.filter((s) => (s?.url || '').trim()).length;

  return (
    <div data-testid="profile-inventory-body" style={{ padding: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <div style={{ fontSize: 15, fontWeight: 'bold', letterSpacing: 2, textTransform: 'uppercase' }}>
          🎒 Items <span style={{ fontSize: 11, opacity: 0.7 }}>({filledCount}/{MAX})</span>
        </div>
        <button
          data-testid="inventory-save"
          onClick={save}
          disabled={busy || loading}
          style={{
            background: '#4a3a1a', color: '#ffe1a0',
            border: '1px solid #a08a30', padding: '4px 14px',
            fontWeight: 'bold', cursor: (busy || loading) ? 'wait' : 'pointer',
            opacity: (busy || loading) ? 0.7 : 1,
          }}
        >{busy ? 'Saving…' : 'SAVE'}</button>
      </div>
      {err && <div data-testid="inventory-error"
                    style={{ color: '#7a1f1f', fontSize: 12, marginBottom: 6 }}>{err}</div>}
      {note && <div data-testid="inventory-note"
                    style={{ color: '#1c5a1c', fontSize: 12, marginBottom: 6 }}>{note}</div>}
      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', opacity: 0.7 }}>Loading…</div>
      ) : (
        <div
          data-testid="inventory-slots"
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 6, marginBottom: 12,
          }}
        >
          {items.map((slot, i) => {
            const url = slot?.url || '';
            const name = slot?.name || '';
            const hasUrl = !!url;
            return (
              <div
                key={i}
                data-testid={`inventory-slot-${i}`}
                style={{
                  border: '1px solid #7a5a1a',
                  background: hasUrl ? '#faf3d8' : '#f4e9c7',
                  padding: 4, display: 'flex', gap: 4, alignItems: 'center',
                  fontSize: 11,
                }}
              >
                <div
                  style={{
                    width: 44, height: 44, minWidth: 44,
                    background: '#e0d3a3', border: '1px solid #7a5a1a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {hasUrl ? (
                    <img
                      src={url}
                      alt={name || `slot-${i}`}
                      style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span style={{ fontSize: 22, opacity: 0.35 }}>◇</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input
                    data-testid={`inventory-slot-url-${i}`}
                    value={url}
                    onChange={(e) => updateSlot(i, 'url', e.target.value)}
                    placeholder={`Item ${i + 1} image URL`}
                    style={{
                      width: '100%', padding: '2px 4px', fontSize: 10,
                      border: '1px solid #5a4a1d',
                      fontFamily: 'inherit', boxSizing: 'border-box',
                    }}
                  />
                  <input
                    data-testid={`inventory-slot-name-${i}`}
                    value={name}
                    onChange={(e) => updateSlot(i, 'name', e.target.value)}
                    placeholder="Name (optional)"
                    style={{
                      width: '100%', padding: '2px 4px', fontSize: 10, marginTop: 2,
                      border: '1px solid #5a4a1d',
                      fontFamily: 'inherit', boxSizing: 'border-box',
                    }}
                  />
                </div>
                {hasUrl && (
                  <button
                    data-testid={`inventory-slot-clear-${i}`}
                    onClick={() => { updateSlot(i, 'url', ''); updateSlot(i, 'name', ''); }}
                    title="Clear this slot"
                    style={{
                      background: '#5d2a2a', color: '#fce2e2', border: '1px solid #3a1a1a',
                      fontSize: 10, padding: '2px 5px', cursor: 'pointer',
                    }}
                  >×</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── CAUGHT POKÉMON ── */}
      <div
        style={{
          borderTop: '1px dashed #7a5a1a', marginTop: 6, paddingTop: 8,
          marginBottom: 4,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 'bold', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
          🔴 Caught Pokémon <span style={{ fontSize: 11, opacity: 0.7 }}>({pokemon.length})</span>
        </div>
        {pokemon.length === 0 ? (
          <div style={{ padding: 8, fontSize: 12, fontStyle: 'italic', opacity: 0.7 }}>
            No captures yet. Weaken a Pokémon or Pokémorph below 5 HP, then use <code>/throwball &lt;name&gt;</code> to catch it.
          </div>
        ) : (
          <div
            data-testid="inventory-caught-pokemon"
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 6,
            }}
          >
            {pokemon.map((p) => (
              <div
                key={p.npc}
                data-testid={`inventory-pokemon-${p.npc}`}
                style={{
                  background: p.subtype === 'pokemorph' ? '#f5d6e3' : '#dceac0',
                  border: '1px solid #7a5a1a', padding: 6,
                  fontSize: 11,
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: 12 }}>{p.nickname || p.npc}</div>
                <div style={{ opacity: 0.7, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {p.subtype}
                </div>
                <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '1px 6px', background: '#faf3d8',
                    border: '1px solid #a08a30', fontSize: 10,
                  }}>❤ HP {p.hp_max}</span>
                  <span style={{
                    padding: '1px 6px', background: '#faf3d8',
                    border: '1px solid #a08a30', fontSize: 10,
                  }}>♥ {p.affection}</span>
                  <span style={{
                    padding: '1px 6px', background: '#faf3d8',
                    border: '1px solid #a08a30', fontSize: 10,
                  }}>⚔ {p.dislike}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 10, fontStyle: 'italic', opacity: 0.7 }}>
          Tip: keep chatting with your caught Pokémon to grow affection. Take them to <b>Pokemon_Stadium</b> and use <code>/stadium &lt;pokemon&gt;</code> to challenge other trainers.
        </div>
      </div>
    </div>
  );
};

export default InventoryTabBody;
